import type { PatientResponse } from '../../../api/generated';
import type { RoomProperty } from '../../../store/roomProperties/roomPropertiesSlice';

// Narrowed rather than read straight off the type: the generated client asserts
// `preferredStaffIds: string[]`, it does not check it, so a payload that does not match still
// arrives typed as if it did. A payload that is not an array yields nothing; a mixed array keeps
// its valid ids and drops the rest.
//
// Dropping is loud on purpose, but be clear about what that buys: it makes the loss traceable,
// not impossible. What this returns is what a save writes back, so a payload the app cannot read
// still clears the field on the next edit, now with a console trail instead of in silence. That
// is what separates this field from `patientRoomNeedIds`, which is read raw: room needs are
// resolved against a table at render, where a miss costs a row, while these ids go into the form
// and come back out again on save.
//
// An absent field is loud too, now that the backend declares it required (REH-284): before it
// shipped, absence was the normal case and stayed quiet — today it means the response broke its
// contract, and staying quiet about that would clear the patient's preference on the next save.
export const readPreferredStaffIds = (patient: PatientResponse): string[] => {
  const raw: unknown = patient.preferredStaffIds;
  if (!Array.isArray(raw)) {
    console.error('preferredStaffIds: expected an array of ids, got', raw);
    return [];
  }
  // Annotated because Array.isArray narrows `unknown` to `any[]`, so the predicate parameter
  // would otherwise be an implicit `any` that the disabled no-unsafe-* rules would not catch.
  const ids = raw.filter((id: unknown): id is string => typeof id === 'string');
  if (ids.length !== raw.length) {
    console.error('preferredStaffIds: dropped entries that were not id strings', raw);
  }
  return ids;
};

export interface FormState {
  name: string;
  cpr: string;
  email: string;
  phone: string;
  comment: string;
  roomNeeds: RoomProperty[];
  // Raw ids, deliberately not resolved StaffResponse objects. Resolving on read and writing the
  // resolved list back would delete every id that happened not to resolve, and the roster is not
  // always complete when this form is seeded: the fetch is async, so there is a window on every
  // open, it can fail outright, and it is location-scoped, so a patient who moved location has
  // ids no roster will ever cover. Ids in, ids out: the form can only add or explicitly remove
  // one. Resolution happens at render, where a miss costs a row rather than the patient's data.
  preferredStaffIds: string[];
}

// A factory, not a shared constant. A singleton would hand every create-mode form the same array
// instances via the shallow spreads in usePatientForm, so one future in-place push would corrupt
// the module for every later create in the session.
export const buildEmptyForm = (): FormState => ({
  name: '',
  cpr: '',
  email: '',
  phone: '',
  comment: '',
  roomNeeds: [],
  preferredStaffIds: []
});

export const formFromPatient = (patient: PatientResponse, roomNeeds: RoomProperty[]): FormState => ({
  name: patient.name,
  cpr: patient.cprNumber,
  email: patient.email ?? '',
  phone: patient.phoneNumber ?? '',
  comment: patient.note ?? '',
  roomNeeds,
  preferredStaffIds: readPreferredStaffIds(patient)
});

export type PatientCreatePrefill = { name: string } | { cpr: string };

export interface PatientDetailsCardProps {
  patient?: PatientResponse | null;
  locationId: string;
  mode: 'view' | 'edit' | 'create';
  createPrefill?: PatientCreatePrefill;
  onEditStart: () => void;
  onEditEnd: () => void;
  onPatientUpdated?: () => void;
  onPatientCreated?: (created: PatientResponse) => void | Promise<void>;
}
